import type { Address } from "viem"
import { useMemo, useState } from "react"
import * as Form from "@radix-ui/react-form"
import { getAddress, isAddress, isAddressEqual } from "viem"

import type { ChainConfig, MinimalToken, Token, TokenName, TokenSymbol } from "@/domains/core"
import { Button } from "@/components/button"
import { HideWhenFalse, HideWhenTrue, ShowWhenTrue } from "@/components/conditionals"
import { Dialog, DialogContent, DialogTrigger } from "@/components/dialog"
import { ScrollArea } from "@/components/scroll-area"
import { Search } from "@/components/search"
import { Separator } from "@/components/separator"
import { TokenIcon } from "@/components/token-icon"
import { MultilineMessageBox } from "@/domains/common/components/multi-line-message-box"
import { ShortMessageBox } from "@/domains/common/components/short-message-box"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/domains/common/components/tabs"
import { CloseCircleIcon, ExternalLinkIcon, PlusCircleIcon, WarningSquareIcon } from "@/domains/icons"
import { cn, dollars, formatCurrency, trimEthAddress } from "@/domains/library"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useLocalTokens } from "@/hooks/use-local-tokens"

import { tokenSearchFilter } from "./token-search-filter"

/**
 *
 * Testing tokens
 * "0x662B3d319e693aA578EDd4BD8a5c9395BC49e9F4" Goggles isSwapToken = true
 * "0x617A3532f836E524C3c4675f54d9C40F745BBBCD" Arobase isSwapToken = false
 *
 * Unknown tokens for testing
 * - 0x1fd7a725eb52a1963c9b636f95b57533f1507ee1
 * - 0xbc47e8f5c9e0532632103e2cefb668a33018dd09
 * - 0x9fC071cE771c7B27b7d9A57C32c0a84c18200F8a
 * - 0xae75A438b2E0cB8Bb01Ec1E1e376De11D44477CC SUSHI
 *
 * @returns
 */

const TokenDialog: React.FC<{
  account: Address
  chainConfig: ChainConfig
  className?: string
  children: React.ReactNode
  filterTokens?: (token: Token) => boolean
  governanceToken: TokenName
  nativeToken: MinimalToken & TokenName
  wrappedNativeToken: TokenSymbol
  quickSelectTokens: Token[]
  showLocalAssets?: boolean
  tokens: Array<Token>
  onTokenSelect: (token: Token | null) => void
}> = ({
  account,
  chainConfig,
  children,
  className,
  filterTokens,
  governanceToken,
  quickSelectTokens = [],
  nativeToken,
  wrappedNativeToken,
  showLocalAssets = false,
  tokens,
  onTokenSelect,
}) => {
  // Internal state (everything else should come from outside world)
  const [searchInternalTokens, setSearchInternalTokens] = useState("")
  const [searchLocalAssets, setSearchLocalAssets] = useState("")
  const [localSearchError, setLocalSearchError] = useState("")
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("listed")

  const listedTokens = useMemo(() => {
    return tokens.filter((token) => Boolean(token.isSwapToken) || isAddressEqual(token.address, nativeToken.address))
  }, [tokens, nativeToken])

  const unlistedTokens = useMemo(() => {
    return tokens.filter((token) => {
      if (isAddressEqual(token.address, nativeToken.address)) {
        return false
      }
      return !token.isSwapToken
    })
  }, [nativeToken.address, tokens])

  // local tokens
  const onTokenAdded = (_token: Token) => {
    setSearchLocalAssets("")
  }
  const onTokenRemoved = (_token: Token) => {}
  const equalensAddress = chainConfig.equalizer.lenses.multiBalance

  const { localTokens, handleAddToken, handleRemoveToken } = useLocalTokens({
    account,
    equalensAddress,
    onTokenAdded,
    onTokenRemoved,
  })
  // 0x94fBE860aD699670A2293D194CF1376EF58C014a
  // console.log("localTokens", localTokens)

  // First we apply the filter
  const filteredTokens = filterTokens ? tokens.filter(filterTokens) : tokens
  const filteredListedTokens = filterTokens ? listedTokens.filter(filterTokens) : listedTokens
  const filteredUnlistedTokens = filterTokens ? unlistedTokens.filter(filterTokens) : unlistedTokens

  // Then we apply the search filter
  const searchListedTokens = tokenSearchFilter(
    filteredListedTokens,
    searchInternalTokens,
    nativeToken.symbol,
    wrappedNativeToken.symbol,
  )
  const searchUnlistedTokens = tokenSearchFilter(
    filteredUnlistedTokens,
    searchInternalTokens,
    nativeToken.symbol,
    wrappedNativeToken.symbol,
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const ethereum =
    typeof window !== "undefined" ? (window as Window & { ethereum?: { isMetaMask?: boolean } }).ethereum : undefined

  const onLocalTokenSelect = (token: Token) => {
    onTokenSelect(token)
  }

  /**
   * Process the search string for valid addresses and add the token if it is a valid address
   * Otherwise pass the search string through to the search field and filter as normal
   *
   * test addresses on base:
   * "0x27D2DECb4bFC9C76F0309b8E88dec3a601Fe25a8" BALD
   * "0xEB466342C4d449BC9f53A865D5Cb90586f405215" axlUSDC
   */
  const onLocalAssetsSearchChange = async (search: string) => {
    // Checks that don't require feedback
    if (typeof search !== "string" || search.length !== 42) {
      setLocalSearchError("")
      return
    }

    // if not an address then let them know
    const isValidAddress = isAddress(search, { strict: false }) // allow casing issues

    if (!isValidAddress) {
      setLocalSearchError("Not a valid address")
      setSearchLocalAssets(search)
      return
    }

    const searchAddress = getAddress(search) // convert to proper case

    // ------------------------------------------------------------
    // Is this a known system token?
    const foundSystemToken = filteredTokens.find((token) => isAddressEqual(token.address, searchAddress))
    // They are trying to import a whitelisted token, so lets switch tabs and select it
    if (foundSystemToken?.isSwapToken) {
      setActiveTab("listed")
      setSearchInternalTokens(searchAddress)
      setSearchLocalAssets("")
      setLocalSearchError("")
      return
    }

    // They are trying to import a non-whitelisted token, so lets switch tabs and select it
    if (foundSystemToken && !foundSystemToken.isSwapToken) {
      setActiveTab("unlisted")
      setSearchInternalTokens(getAddress(searchAddress))
      setSearchLocalAssets("")
      setLocalSearchError("")
      return
    }
    // ------------------------------------------------------------

    // is this a known local token? (are they adding twice)
    const foundLocalToken = localTokens.find((token) => isAddressEqual(token.address, searchAddress))
    if (foundLocalToken) {
      setLocalSearchError(`${foundLocalToken.symbol} is already a local token`)
      setSearchLocalAssets("")
      return
    }

    // lets add the token and be away with it
    console.log("adding token", search, searchAddress)
    void handleAddToken(searchAddress)
    setSearchLocalAssets("")
    setLocalSearchError("")
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className={className}>{children}</DialogTrigger>
      <DialogContent className="gap-0 border border-p400-light bg-p400 pt-10 text-t100" title="Select Token">
        <ShowWhenTrue when={!showLocalAssets}>
          <div className="flex-rowc mb-1 justify-start text-lg">Select token</div>
        </ShowWhenTrue>

        <Tabs defaultValue="listed" value={activeTab} onValueChange={setActiveTab}>
          <ShowWhenTrue when={showLocalAssets}>
            <div>
              <TabsList className="mb-6 grid grid-cols-3 gap-2" defaultValue="listed">
                <TabsTrigger value="listed">Listed</TabsTrigger>
                <TabsTrigger value="imported">Imported</TabsTrigger>
                <TabsTrigger value="unlisted">Unlisted</TabsTrigger>
              </TabsList>
            </div>
          </ShowWhenTrue>

          <TabsContent value="listed" className="" asChild>
            <div className="flex min-h-[540px] flex-col gap-6">
              <Form.Root
                className="w-full"
                onSubmit={(event) => {
                  event.preventDefault()
                }}
              >
                <Search
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                  value={searchInternalTokens}
                  onChange={(e) => setSearchInternalTokens(e.target.value)}
                />
              </Form.Root>

              <HideWhenTrue when={quickSelectTokens.length === 0}>
                <div className="hidden md:block">
                  <p className="mb-2 ml-1 flex flex-row items-center justify-start text-xs text-t200">Trending</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4 md:grid-cols-5">
                    {quickSelectTokens.map((token: Token) => {
                      return (
                        <Button
                          key={token.address.toLowerCase()}
                          variant={"neutral"}
                          onClick={() => {
                            onTokenSelect(token)
                            setOpen(false)
                          }}
                          className="flex flex-row items-center justify-start gap-2"
                        >
                          <TokenIcon token={token} className="shrink" square={18} calledFrom="TokenDialog" />
                          <div className="text-sm text-t100">{token.symbol}</div>
                        </Button>
                      )
                    })}
                  </div>
                </div>
                <Separator
                  className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
                after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
                after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
                  decorative
                />
              </HideWhenTrue>
              <TokenList
                tokens={searchListedTokens}
                onTokenSelect={onTokenSelect}
                setOpen={setOpen}
                ethereum={ethereum}
                chainConfig={chainConfig}
                TokenIcons={null}
                handleRemoveToken={handleRemoveToken}
              />
            </div>
          </TabsContent>

          <TabsContent value="imported" className="gap-6" asChild>
            <div className="flex min-h-[480px] flex-col gap-6">
              <Form.Root
                className=""
                onSubmit={(event) => {
                  event.preventDefault()
                }}
              >
                <Search
                  placeholder={"Enter an address"}
                  value={searchLocalAssets}
                  onChange={(e) => onLocalAssetsSearchChange(e.target.value)}
                />
                <ShowWhenTrue when={localSearchError.length > 0}>
                  <ShortMessageBox icon={<WarningSquareIcon size={16} />} variant="error" size="sm" className="mt-4">
                    {localSearchError}
                  </ShortMessageBox>
                </ShowWhenTrue>
              </Form.Root>

              <TokenList
                tokens={localTokens}
                onTokenSelect={onLocalTokenSelect}
                setOpen={setOpen}
                ethereum={ethereum}
                chainConfig={chainConfig}
                TokenIcons={LocalTokenIcons}
                handleRemoveToken={handleRemoveToken}
                scrollHeight="large"
              />
            </div>
          </TabsContent>

          <TabsContent value="unlisted" className="" asChild>
            <div className="flex min-h-[500px] flex-col gap-6">
              <Form.Root
                className="w-full"
                onSubmit={(event) => {
                  event.preventDefault()
                }}
              >
                <MultilineMessageBox icon={<WarningSquareIcon size={20} />} variant="error" size="md" className="mb-4">
                  Unlisted tokens are possibly unsafe. Anyone can create a token via our Liquidity Pools, use at your
                  own risk. Please contact the team if your token appears here incorrectly.
                </MultilineMessageBox>
                <Search
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                  value={searchInternalTokens}
                  onChange={(e) => setSearchInternalTokens(e.target.value)}
                />
              </Form.Root>

              <TokenList
                tokens={searchUnlistedTokens}
                onTokenSelect={onTokenSelect}
                setOpen={setOpen}
                ethereum={ethereum}
                chainConfig={chainConfig}
                TokenIcons={null}
                handleRemoveToken={handleRemoveToken}
                scrollHeight="large"
              />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

const LocalTokenIcons = ({
  token,
  handleRemoveToken,
}: {
  token: Token
  handleRemoveToken: (address: string) => void
}) => {
  const chainConfig = useChainConfig()
  return (
    <div className="flex grow flex-row items-center justify-end gap-2 pr-2">
      <CloseCircleIcon
        size={16}
        className="cursor-pointer text-t200"
        onClick={async (event) => {
          event.stopPropagation()
          event.preventDefault()
          // console.log("token", token)
          handleRemoveToken(token.address)
        }}
      />
      <ExternalLinkIcon
        size={16}
        className="cursor-pointer text-t200"
        onClick={async (event) => {
          event.stopPropagation()
          event.preventDefault()

          if (!chainConfig.scanner) {
            console.error("No scanner for chain", chainConfig.name)
            return
          }
          const tokenUrl = chainConfig.scanner.tokenUrl.replace("{tokenAddress}", token.address)
          window.open(tokenUrl, "_blank")
        }}
      />
    </div>
  )
  return null
}

type TokenListScrollHeight = "default" | "large"
const TokenList = ({
  tokens,
  onTokenSelect,
  setOpen,
  ethereum,
  chainConfig,
  TokenIcons,
  handleRemoveToken,
  scrollHeight = "default",
}: {
  tokens: Token[]
  onTokenSelect: (token: Token) => void
  setOpen: (open: boolean) => void
  ethereum: any
  chainConfig: ChainConfig
  TokenIcons?: any
  handleRemoveToken: (address: Address) => void
  scrollHeight?: TokenListScrollHeight
}) => {
  const scrollAreaHeight = scrollHeight === "large" ? "h-[400px]" : "h-[280px]"
  return (
    <div>
      <p className="mb-2 ml-1 flex flex-row items-center justify-start text-xs text-t200">Tokens</p>
      <ScrollArea className={cn("-mx-2 rounded-md border-primary pr-3", scrollAreaHeight)}>
        <div className="flex flex-col items-center space-y-1">
          {tokens.map((token: Token) => {
            return (
              <div
                key={token.address.toLowerCase()}
                className="flex-rowc w-full cursor-pointer items-center gap-2 rounded-xl bg-transparent px-3 py-2 text-sm transition-colors hover:bg-p400-light/50"
                onClick={() => {
                  onTokenSelect(token)
                  setOpen(false)
                }}
              >
                <div className="flex h-[32px] w-[32px] flex-row items-center justify-between">
                  <TokenIcon token={token} className="" square={32} calledFrom="TokenList" />
                </div>
                <div className="flex grow flex-col items-start normal-case">
                  <div className="text-sm text-t100">
                    {token.symbol ? token.symbol : "Not available"}{" "}
                    <span className="truncate text-t200">({token.name ? token.name : "🤷"})</span>
                  </div>
                  <div className="hidden text-xs text-t200 md:block">{token.address}</div>
                  <div className="block text-xs text-t200 md:hidden">{trimEthAddress(token.address, 4, 4)}</div>
                </div>
                {TokenIcons ? <TokenIcons token={token} handleRemoveToken={handleRemoveToken} /> : null}
                <div className="flex flex-col items-end font-normal  normal-case">
                  <div className="text-sm text-t100">{formatCurrency(token.balance ?? 0, 4)}</div>
                  <div className="text-xs text-t200">~{dollars(token.balanceUsdValue ?? 0)}</div>
                </div>
                <HideWhenFalse when={Boolean(ethereum)}>
                  <div className="">
                    <PlusCircleIcon
                      size={16}
                      className="ml-4 cursor-pointer text-t200"
                      onClick={async (event) => {
                        event.stopPropagation()
                        event.preventDefault()

                        if (ethereum) {
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                          await ethereum.request({
                            method: "wallet_watchAsset",
                            params: {
                              type: "ERC20", // Initially only supports ERC20, but eventually more!
                              options: {
                                address: token.address, // The address that the token is at.
                                symbol: token.symbol, // A ticker symbol or shorthand, up to 5 chars.
                                decimals: token.decimals,
                                image: `https://cdn.jsdelivr.net/gh/unified-defi/equalizer-tokens@latest/${chainConfig.slug}/tokens/128x128/${token.address.toLowerCase}.png`, // A string url of the token logo
                              },
                            },
                          })
                        }
                      }}
                    />
                  </div>
                </HideWhenFalse>
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

export default TokenDialog
